import {
    IManageReportAccessModalProps,
    IReportAccessUserSelectionOptions,
    IUserRecord,
    IUserWithReportAccessData,
    TActiveClientId,
    TManageReportAccessActions,
    TManageReportAccessInitialState,
    TReportAccessTypeMenuItem,
    ReportsAccessTypes,
    ReportVisibilityTypes
} from '@/ts/common-types'
import { Button, Card, Drawer, Dropdown, Select, Typography, Spin, Popconfirm, Radio, Modal } from 'antd'
import { DownOutlined, CheckOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import React, { useEffect, useReducer, useState } from 'react'
const { Text, Title } = Typography;
import { titleCase } from '@/ts/utils';
import { MANAGE_REPORT_ACCESS_INITIAL_STATE, REPORT_ACCESS_TYPE_DETAILS_NOTES } from '@/ts/constants';
import { addReportAccess, getClientUsers, getUsersWithReportAccess, removeReportAccess, updateReportAccess, clearReportUserRestrictions } from '@/ts/retrieve-data';
import { showToast } from '@/ts/modals';
import * as Sentry from "@sentry/react";
import { useSelector } from 'react-redux';
import { debounce } from 'underscore';




const ManageReportAccessModal = ({ open,
    setOpen,
    title,
    selectedReportData,
    setGetUpdatedReports,
    userData }:
    IManageReportAccessModalProps) => {

    const iconStyles = { fontSize: 18 }
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const [usersWithAccess, setUsersWithAccess] = useState<IUserWithReportAccessData[]>([]);
    const [usersOptions, setUsersOptions] = useState<IReportAccessUserSelectionOptions[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [userOptionsLoading, setUserOptionsLoading] = useState<boolean>(true);
    const [fetchUsersWithAccess, setFetchUsersWithAccess] = useState<boolean>(false);
    const [accessType, setAccessType] = useState<string>(selectedReportData?.access_type || ReportsAccessTypes.GENERAL);
    const [visibility, setVisibility] = useState<string>(selectedReportData?.visibility || ReportVisibilityTypes.SHOW);
    const [openRemovePopConfirmId, setOpenRemovePopConfirmId] = useState<number | null>(null);
    const [showClearanceWarning, setShowClearanceWarning] = useState<boolean>(false);
    const [pendingVisibilityChange, setPendingVisibilityChange] = useState<string | null>(null);
    const [clearedUsers, setClearedUsers] = useState<any[]>([]);


    const loadingStatesReducer = (
        state: TManageReportAccessInitialState,
        action: TManageReportAccessActions
    ) => {
        switch (action.type) {
            case "SET_USERS_WITH_ACCESS_LOADING":
                return { ...state, usersWithAccessLoading: action.payload };
            case "SET_REMOVE_USERS_ACCESS_LOADING":
                return { ...state, removeUserAccessLoading: action.payload };
            case "SET_ADD_OR_CHANGE_ACCESS_LOADING":
                return { ...state, addOrChangeAccessLoading: action.payload };
            default:
                throw new Error(
                    `Unhandled action type: ${(action as any).type}`
                );
        }
    };

    const [loadingState, setLoadingState] = useReducer(
        loadingStatesReducer,
        MANAGE_REPORT_ACCESS_INITIAL_STATE
    );

    const {
        usersWithAccessLoading,
        removeUserAccessLoading,
        addOrChangeAccessLoading
    } = loadingState;

    const items: TReportAccessTypeMenuItem[] = [
        {
            key: ReportsAccessTypes.GENERAL,
            label: (
                <div className="d-flex items-center justify-between px-2 py-2 gap-2">
                    <div>General</div>
                    <div className="text-primary">{selectedReportData?.access_type === ReportsAccessTypes.GENERAL && <CheckOutlined />}</div>
                </div>
            ),
        },
        {
            type: 'divider',
        },
        {
            key: ReportsAccessTypes.RESTRICTED,
            label: (
                <div className="d-flex items-center justify-between px-2 py-2 gap-2">
                    <div>Restricted</div>
                    <div className="text-primary">{selectedReportData?.access_type === ReportsAccessTypes.RESTRICTED && <CheckOutlined />}</div>
                </div>
            ),
        },
    ];

    useEffect(() => {
        if (selectedReportData?.access_type) {
            setAccessType(selectedReportData.access_type);
        }
        if (selectedReportData?.visibility) {
            setVisibility(selectedReportData.visibility);
        }
    }, [selectedReportData]);

    useEffect(() => {


        if (accessType !== ReportsAccessTypes.RESTRICTED) return;

        const getUsersWithAccess = async () => {
            try {
                setLoadingState({ type: "SET_USERS_WITH_ACCESS_LOADING", payload: true });
                const response = await getUsersWithReportAccess(selectedReportData?.report_id, visibility);
                // Deduplicate users by user_id to prevent duplicates
                const uniqueUsers = (response.rows || []).reduce((acc: any[], user: any) => {
                    const existingUser = acc.find(u => u.user_id === user.user_id);
                    if (!existingUser) {
                        acc.push(user);
                    } else {
                        console.warn('Duplicate user found:', user.email, 'keeping first occurrence');
                    }
                    return acc;
                }, []);
                
                setUsersWithAccess(uniqueUsers);

            } catch (err) {
                console.error('Error fetching users with access:', err);
                showToast("danger", (err as Error).message);
                Sentry.captureException(err);
            } finally {
                setLoadingState({ type: "SET_USERS_WITH_ACCESS_LOADING", payload: false });
            }
        }

        getUsersWithAccess();



    }, [selectedReportData, fetchUsersWithAccess, accessType, visibility])



    useEffect(() => {
        if (accessType !== ReportsAccessTypes.RESTRICTED) return;

        const fetchUsers = debounce(async () => {
            try {
                setUserOptionsLoading(true);
                const page = 1;
                const pageSize = 25;
                const response = await getClientUsers(parseInt(activeClientId), page, pageSize, searchQuery);

                const fetchedOptions = response.users?.map((user: IUserRecord) => {
                    if (userData.user_id !== user.user_id) {
                        return {
                            label: (
                                <div className="d-flex items-center gap-2">
                                    <Text>{user.first_name} {user.last_name}</Text>
                                    <Text type="secondary">{user.email}</Text>
                                </div>
                            ),
                            value: user.user_id,
                            disabled: false,
                        }
                    }
                }).filter(Boolean);

                const accessUsersOptions = usersWithAccess?.map((user) => ({
                    label: (
                        <div className="d-flex items-center gap-2 opacity-50">
                            <Text>{user.first_name} {user.last_name}</Text>
                            <Text type="secondary">{user.email}</Text>
                        </div>
                    ),
                    value: user.user_id,
                    disabled: true,
                }));

                const mergedMap = new Map<number | string, IReportAccessUserSelectionOptions>();

                if (accessUsersOptions && fetchedOptions) {
                    [...accessUsersOptions, ...fetchedOptions].forEach((option) => {
                        if (!mergedMap.has(option.value)) {
                            mergedMap.set(option.value, option);
                        }
                    });
                }

                // Ensure selected values are preserved even if not in current fetch
                selectedUsers.forEach((selectedValue) => {
                    if (!mergedMap.has(selectedValue)) {
                        const selectedInState = usersOptions?.find(opt => opt.value === selectedValue);
                        if (selectedInState) {
                            mergedMap.set(selectedValue, selectedInState);
                        }
                    }
                });

                setUsersOptions(Array.from(mergedMap.values()));
            } catch (err) {
                showToast("danger", (err as Error).message);
                Sentry.captureException(err);
            } finally {
                setUserOptionsLoading(false);
            }
        }, 400);

        fetchUsers();

        return () => {
            fetchUsers.cancel();
        };
    }, [searchQuery, selectedReportData?.access_type, activeClientId, usersWithAccess, selectedUsers]);


    const handleAccessChange = async (accessType: string, visibilityParam?: string) => {
        try {
            const currentVisibility = visibilityParam || visibility;
            const payload = {
                reportAccess: accessType,
                ...(accessType === ReportsAccessTypes.RESTRICTED && { visibility: currentVisibility })
            };
            const response = await updateReportAccess(selectedReportData?.report_id, accessType, payload);
            if (response.status) {
                showToast("success", response.message || "Report access updated successfully");
                setGetUpdatedReports((prev) => !prev);
                setAccessType("");
            }
            else {
                const errorMessage = typeof response.message === 'string' 
                    ? response.message 
                    : 'Failed to update report access';
                showToast("danger", errorMessage);
            }

            setOpen(false);
            setAccessType("");

        } catch (err) {
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
        }
    };

    const handleUserChange = (value: string[]) => {
        setSelectedUsers(value);
        setSearchQuery("");
    };

    const handleUserSearch = (value: string) => {
        setSearchQuery(value);
    };

    const handleSubmit = async () => {
        try {
            setLoadingState({ type: "SET_ADD_OR_CHANGE_ACCESS_LOADING", payload: true });
            
            if (accessType !== selectedReportData?.access_type || 
                (accessType === ReportsAccessTypes.RESTRICTED && visibility !== selectedReportData?.visibility)) {
                await handleAccessChange(accessType, visibility);
            }

            if (selectedUsers.length) {

                const reportId = selectedReportData?.report_id;
                const usersToAdd = selectedUsers.map((userId) => {
                    return {
                        userId: +userId,
                        reportId,
                        status: true
                    }
                });

                // Prevent adding yourself to the "hide" list
                if (visibility === ReportVisibilityTypes.HIDE) {
                    const filteredUsers = usersToAdd.filter(user => user.userId !== userData.user_id);
                    if (filteredUsers.length !== usersToAdd.length) {
                        showToast("warning", "You cannot hide a report from yourself. Removing your user ID from the list.");
                    }
                    usersToAdd.splice(0, usersToAdd.length, ...filteredUsers);
                }

                const response = await addReportAccess(usersToAdd, reportId);

                if (response.status) {
                    showToast("success", response.message);
                    setSelectedUsers([]);
                    // Single refresh should be enough
                    setFetchUsersWithAccess((prev) => !prev);
                }
                else {
                    const errorMessage = typeof response.message === 'string' 
                        ? response.message 
                        : 'Failed to add report access';
                    showToast("danger", errorMessage);
                }
            }


        } catch (err) {
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
        } finally {
            setLoadingState({ type: "SET_ADD_OR_CHANGE_ACCESS_LOADING", payload: false });
        }
    }


    const handleRemoveUserAccess = async (userId: number, reportId: string) => {
        try {
            setLoadingState({ type: "SET_REMOVE_USERS_ACCESS_LOADING", payload: true });
            const response = await removeReportAccess(reportId, userId);

            if (response.status) {
                showToast("success", response.message);
                
                // If the report was converted to General, update the UI state
                if (response.convertedToGeneral) {
                    setAccessType(ReportsAccessTypes.GENERAL);
                    setVisibility(ReportVisibilityTypes.SHOW);
                    setGetUpdatedReports((prev) => !prev);
                }
                
                setFetchUsersWithAccess((prev) => !prev);
            }
            else {
                const errorMessage = typeof response.message === 'string' 
                    ? response.message 
                    : 'Failed to remove report access';
                showToast("danger", errorMessage);
            }

        } catch (err) {
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
        }
        finally {
            setLoadingState({ type: "SET_REMOVE_USERS_ACCESS_LOADING", payload: false });
        }
    }

    const handleRemoveUserWithConfirmation = (userId: number, reportId: string) => {
        // Check if this is the last user for a restricted report
        const isLastUser = usersWithAccess.length === 1;
        const isRestrictedReport = accessType === ReportsAccessTypes.RESTRICTED;
        
        if (isLastUser && isRestrictedReport) {
            Modal.confirm({
                title: 'Remove Last User',
                content: (
                    <div>
                        <p>This is the last user for this restricted report. Removing them will automatically convert the report to General access, making it visible to everyone.</p>
                        <p><strong>Are you sure you want to continue?</strong></p>
                    </div>
                ),
                okText: 'Yes, Convert to General',
                cancelText: 'Cancel',
                onOk: () => handleRemoveUserAccess(userId, reportId),
                okButtonProps: { danger: true }
            });
        } else {
            handleRemoveUserAccess(userId, reportId);
        }
    }

    const handleVisibilityChange = async (newVisibility: string) => {
        // Check if we need to clear existing users
        const hasExistingUsers = usersWithAccess.length > 0;
        const isVisibilityChange = visibility !== newVisibility;
        const isRestrictedReport = accessType === ReportsAccessTypes.RESTRICTED;

        if (hasExistingUsers && isVisibilityChange && isRestrictedReport) {
            // Show confirmation dialog with users to be cleared
            setPendingVisibilityChange(newVisibility);
            setShowClearanceWarning(true);
        } else {
            // No users to clear, proceed with change
            setVisibility(newVisibility);
        }
    }

    const handleClearanceConfirm = async () => {
        try {
            if (!selectedReportData?.report_id) {
                showToast("danger", "No report selected");
                return;
            }

            // Clear all user restrictions
            let response;
            try {
                response = await clearReportUserRestrictions(selectedReportData.report_id);
            } catch (apiError) {
                showToast("danger", "Failed to connect to server. Please try again.");
                return;
            }
            
            if (response && response.status) {
                const clearedCount = response.cleared || 0;
                const clearedUsersList = response.users || [];
                
                showToast("success", `Successfully cleared ${clearedCount} users from restrictions`);
                setClearedUsers(clearedUsersList);
                
                // Update visibility
                if (pendingVisibilityChange) {
                    setVisibility(pendingVisibilityChange);
                }
                
                // Refresh user list
                setFetchUsersWithAccess(prev => !prev);
                
                // Close the modal first
                setShowClearanceWarning(false);
                setPendingVisibilityChange(null);
                
                // Show detailed success message if users were cleared
                if (clearedCount > 0) {
                    setTimeout(() => {
                        Modal.info({
                            title: 'Users Cleared Successfully',
                            content: (
                                <div>
                                    <p>The following {clearedCount} users have been removed from the restriction list:</p>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d9d9d9', padding: '8px', borderRadius: '4px', backgroundColor: '#f6ffed' }}>
                                        {clearedUsersList.map((user: any, index: number) => (
                                            <div key={user.user_id || index} style={{ padding: '4px 0', borderBottom: '1px solid #e8e8e8' }}>
                                                <strong>{user.name || 'Unknown User'}</strong> ({user.email || 'No email'})
                                            </div>
                                        ))}
                                    </div>
                                    <p style={{ marginTop: '12px', color: '#666' }}>
                                        You can now add users to the new restriction list as needed.
                                    </p>
                                </div>
                            ),
                            width: 600,
                        });
                    }, 1000);
                }
            } else {
                const errorMessage = response?.message || 'Failed to clear user restrictions';
                console.error('Clearance failed:', errorMessage);
                showToast("danger", errorMessage);
            }
        } catch (err) {
            console.error('Clearance error:', err);
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
        } finally {
            // Always close the modal and reset state
            setShowClearanceWarning(false);
            setPendingVisibilityChange(null);
        }
    }

    const handleClearanceCancel = () => {
        setShowClearanceWarning(false);
        setPendingVisibilityChange(null);
    }

    const handleOnclose = () => {
        setOpen(false);
    }
    return (
        <div>
            <Drawer
                title={title}
                width={680}
                onClose={() => handleOnclose()}
                open={open}
            >

                <Card title={"Report Access"} size="small" className="p-3 rounded-3">
                    <div>
                        <Dropdown menu={{
                            items,
                            onClick: ({ key }) => setAccessType(key),
                        }} placement="bottom"
                            trigger={['click']}

                        >
                            <Button
                                className='px-2 fw-normal'
                                type="text"
                            >
                                {accessType === ReportsAccessTypes.GENERAL ?
                                    <UnlockOutlined style={iconStyles} />
                                    : <LockOutlined style={iconStyles} />}
                                {titleCase(accessType.length > 0 ? accessType : selectedReportData?.access_type)}
                                <DownOutlined style={{ fontSize: 11 }} />
                            </Button>
                        </Dropdown>
                        <div className='px-2'>
                            <Text type="secondary" className="mt-2">{accessType === ReportsAccessTypes.GENERAL ?
                                REPORT_ACCESS_TYPE_DETAILS_NOTES[ReportsAccessTypes.GENERAL] :
                                REPORT_ACCESS_TYPE_DETAILS_NOTES[ReportsAccessTypes.RESTRICTED]}</Text>
                        </div>

                        {accessType === ReportsAccessTypes.RESTRICTED && (
                            <div className="mt-3">
                                <Title level={5}>Visibility Options</Title>
                                <Radio.Group 
                                    value={visibility} 
                                    onChange={(e) => handleVisibilityChange(e.target.value)}
                                    className="mt-2"
                                >
                                    <Radio value={ReportVisibilityTypes.SHOW}>Show to selected users</Radio>
                                    <Radio value={ReportVisibilityTypes.HIDE}>Hide from selected users</Radio>
                                </Radio.Group>
                            </div>
                        )}

                    </div>
                </Card>

                {
                    accessType === ReportsAccessTypes.RESTRICTED &&
                    <Card className='mt-3'>
                        <div>
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ width: '100%', fontSize: 14 }}
                                placeholder={visibility === ReportVisibilityTypes.HIDE ? "Add People to Hide From" : "Add People"}
                                onChange={handleUserChange}
                                options={userOptionsLoading ?
                                    [{
                                        label: (<div className="d-flex py-2 justify-content-center"><Spin size="small" /></div>),
                                        value: "loader",
                                        disabled: true
                                    }]
                                    : usersOptions}
                                size='large'
                                filterOption={false}
                                onSearch={handleUserSearch}
                                optionFilterProp={"label"}
                                maxTagCount={2}
                                value={selectedUsers}
                                disabled={usersWithAccessLoading}
                                onBlur={() => setSearchQuery("")}
                            />
                            <Title level={5} className="mt-2">
                                {visibility === ReportVisibilityTypes.HIDE ? "People to Hide From" : "People With Access"}
                            </Title>

                            {usersWithAccessLoading ?
                                <div className="d-flex justify-content-center py-2 h-75">
                                    <Spin />
                                </div> :
                                <div className="overflow-auto" style={{ maxHeight: "250px" }}>

                                    {usersWithAccess?.length === 0 ? <Card>
                                        <div className='text-center'><Title level={5}>No Records</Title></div>
                                    </Card> : ""}
                                    {usersWithAccess?.map((user, index) => (
                                        <div key={`${user.user_id}-${user.email}-${index}`} className="mt-2">
                                            <Card size="small" className="w-100">
                                                <div className="d-flex justify-between align-items-center w-100">
                                                    <div>
                                                        <Text>{user.first_name} {user.last_name}</Text>
                                                        <div><Text type='secondary'>{user.email}</Text></div>
                                                    </div>

                                                    <div style={{ marginLeft: 'auto' }}>
                                                        <Popconfirm
                                                            title="Are you sure to remove this user?"
                                                            description={userData.user_id === user.user_id ? (
                                                                <div className='w-75'>
                                                                    <Text type="danger">
                                                                        You will have to contact the administrator to regain access to this report.
                                                                    </Text>
                                                                </div>
                                                            ) : ""}
                                                            open={openRemovePopConfirmId === user.user_id}
                                                            onCancel={() => setOpenRemovePopConfirmId(null)}
                                                            placement="leftTop"
                                                            styles={{ body: { padding: '1rem' } }}
                                                            okText={removeUserAccessLoading ? "Removing..." : "Yes"}
                                                            cancelText="No"
                                                            okButtonProps={{ loading: removeUserAccessLoading }}
                                                            onConfirm={() => {
                                                                handleRemoveUserWithConfirmation(user.user_id, selectedReportData?.report_id);
                                                                setOpenRemovePopConfirmId(null);
                                                            }}
                                                        >
                                                            <Button
                                                                danger
                                                                type="default"
                                                                size="small"
                                                                onClick={() => setOpenRemovePopConfirmId(user.user_id)}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </Popconfirm>


                                                    </div>
                                                </div>
                                            </Card>
                                        </div>
                                    ))}
                                </div>
                            }
                        </div>

                    </Card>
                }
                <div>
                    <Button type="primary" className="mt-3"
                        onClick={() => handleSubmit()}
                        disabled={
                            (accessType === "" || accessType === selectedReportData?.access_type)
                            && selectedUsers.length === 0
                        }
                        loading={addOrChangeAccessLoading}
                    >
                        {addOrChangeAccessLoading ? "Saving..." : "Save"}
                    </Button>
                </div>

            </Drawer>

            {/* User Clearance Confirmation Modal */}
            <Modal
                title="Clear Existing User Restrictions"
                open={showClearanceWarning}
                onOk={handleClearanceConfirm}
                onCancel={handleClearanceCancel}
                okText="Yes, Clear and Continue"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                width={600}
            >
                <div>
                    <p>
                        Changing visibility from <strong>"{visibility}"</strong> to <strong>"{pendingVisibilityChange}"</strong> will remove the following users from the current restriction list:
                    </p>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d9d9d9', padding: '8px', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                        {usersWithAccess.map(user => (
                            <div key={user.user_id} style={{ padding: '4px 0', borderBottom: '1px solid #e8e8e8' }}>
                                <strong>{user.first_name} {user.last_name}</strong> ({user.email})
                            </div>
                        ))}
                    </div>
                    <p style={{ marginTop: '12px', color: '#666' }}>
                        <strong>Note:</strong> These users will be removed from the current restriction list. You can add them back to the new restriction list after the change.
                    </p>
                    <p><strong>Do you want to continue?</strong></p>
                </div>
            </Modal>
        </div>
    )
}

export default ManageReportAccessModal